import { createJSONStorage } from "zustand/middleware";
import { clearAuthResponseData } from "../services/authResponseStorage";

export const USER_PROFILE_STORAGE_KEY = "userProfile";
export const THEME_MODE_STORAGE_KEY = "themeMode";

const SIGNIN_REDIRECT_KEY = "postAuthRedirectTo";
const REDIRECT_CTX_KEY = "redirectCtx";

const LOCAL_SIGN_OUT_KEYS = [
  USER_PROFILE_STORAGE_KEY,
  "mapCenter",
  "userLocation",
  "kbeanToken",
  "artworkData",
  "fcmToken",
  "fcmUserId",
  "fcmEmailKey",
  "fcmTimeZone",
  "fcmLastSyncedAt",
  "inssa:quick-find-preseed-cache:v7",
  // NOTE: intentionally NOT clearing "inssa:geoip:v1" here. It is an IP-derived
  // (network-level, not user-private) approximate location with a 24h TTL. Its
  // whole purpose is to give the map an instant initial center so the
  // nearby-discovery (gems/offers) fetch can start. Wiping it on sign-out forced
  // a live, potentially-cold GeoIP fetch on the next sign-in, leaving the gems
  // button hidden for tens of seconds while the center stayed unresolved.
  "inssa:ads:lastShownAt",
  "inssa:ads:dailyBucketDate",
  "inssa:ads:dailyBucketCount",
  "inssa:ads:capsuleLastShownMap",
];

const AUTH_HANDOFF_KEYS = [SIGNIN_REDIRECT_KEY, REDIRECT_CTX_KEY];

const LOCAL_SIGN_OUT_PREFIXES = [
  "inssa:uncover:categories:v1:",
  "inssa:home:marker-visibility:v1:",
];

const SESSION_SIGN_OUT_KEYS = [
  "inssa:hidden-map-capsule-ids",
  "inssa:home:cluster-return:v1",
  "inssa:find-ai-debug-events:v1",
  "inssa:ads:sessionShownCount",
  "inssa:ads:sessionFirstEligibleSeen",
  "inssa:ads:sessionEligibleViewCount",
  "inssa:ads:sessionCountedInstanceKeys",
];

const SESSION_SIGN_OUT_PREFIXES = [
  "hasShownSuccess",
  "timecapsule-refresh:",
  "timecapsule-initial-draft:",
];

const COOKIE_BACKED_SIGN_OUT_KEYS = [
  "inssa:ads:lastShownAt",
  "inssa:ads:dailyBucketDate",
  "inssa:ads:dailyBucketCount",
  "inssa:ads:capsuleLastShownMap",
];

export const createLocalStorageJSONStorage = () =>
  createJSONStorage(() => localStorage);

export function readStoredJson(key, fallback = null) {
  try {
    const storedValue = localStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : fallback;
  } catch {
    return fallback;
  }
}

function removeStorageKey(storage, key) {
  try {
    storage?.removeItem?.(key);
  } catch {}
}

function removeStoragePrefixes(storage, prefixes) {
  try {
    const keys = [];
    if (typeof storage?.length === "number" && typeof storage?.key === "function") {
      for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (key) keys.push(key);
      }
    } else {
      keys.push(...Object.keys(storage || {}));
    }

    keys.forEach((key) => {
      if (prefixes.some((prefix) => key.startsWith(prefix))) {
        storage.removeItem(key);
      }
    });
  } catch {}
}

function expireCookie(key) {
  if (typeof document === "undefined") return;
  try {
    document.cookie = `${encodeURIComponent(key)}=; max-age=0; path=/; SameSite=Lax`;
  } catch {}
}

export function clearUserSessionStorage() {
  try {
    localStorage.removeItem(USER_PROFILE_STORAGE_KEY);
    clearAuthResponseData();
    localStorage.removeItem("mapCenter");
    localStorage.removeItem("userLocation");
    localStorage.removeItem("kbeanToken");
    localStorage.removeItem("artworkData");
  } catch {}
}

export function clearSignedOutUserStorage({ preserveAuthHandoff = false } = {}) {
  try {
    clearAuthResponseData();
  } catch {}

  const localKeys = preserveAuthHandoff
    ? LOCAL_SIGN_OUT_KEYS
    : [...LOCAL_SIGN_OUT_KEYS, ...AUTH_HANDOFF_KEYS];
  const sessionKeys = preserveAuthHandoff
    ? SESSION_SIGN_OUT_KEYS
    : [...SESSION_SIGN_OUT_KEYS, ...AUTH_HANDOFF_KEYS];

  localKeys.forEach((key) => removeStorageKey(localStorage, key));
  sessionKeys.forEach((key) => removeStorageKey(sessionStorage, key));
  removeStoragePrefixes(localStorage, LOCAL_SIGN_OUT_PREFIXES);
  removeStoragePrefixes(sessionStorage, SESSION_SIGN_OUT_PREFIXES);
  COOKIE_BACKED_SIGN_OUT_KEYS.forEach(expireCookie);
}
